import { isTokenExpired } from '@cretesuite/api-client-auth/src/helpers/isExpired'
import { i18n } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import type { Exchange } from '@urql/core'
import { type AuthConfig, authExchange } from '@urql/exchange-auth'
import {
  type CacheExchangeOpts,
  type StorageAdapter,
  offlineExchange,
} from '@urql/exchange-graphcache'
import { refocusExchange } from '@urql/exchange-refocus'
import { type RetryExchangeOptions, retryExchange } from '@urql/exchange-retry'
import {
  Client,
  type CombinedError,
  errorExchange,
  fetchExchange,
  makeOperation,
} from 'urql'
import { TRACE_HEADERS } from '../../data/app'
import { API_URL } from '../../data/environment'
import introspection from '../../gql/introspection.json'
import { generateTraceId } from '../../helpers/generateTraceId'
import { getTraceIdentityHeaders } from '../../helpers/getTraceIdentityHeaders'
import { getIsOnline } from '../../helpers/networkStatus'
import { reportGraphQLError } from '../../helpers/sentry'
import type { useAccessTokenActions } from '../../hooks/useAccessToken'
import type { TraceRequestActions } from '../../hooks/useTraceRequest'
import { graphcacheConfig } from './cacheConfig'
import { offlineSyncStore, syncTrackerExchange } from './offlineSync'
import { serializeMutationsExchange } from './serializeMutationsExchange'

const schema = introspection as unknown as CacheExchangeOpts['schema']

export type CreateGraphQLClientProps = {
  accessTokenActions: ReturnType<typeof useAccessTokenActions>
  cacheStorage: StorageAdapter
  traceRequestActions: TraceRequestActions
}

const retryOptions: RetryExchangeOptions = {
  initialDelayMs: 1000,
  maxDelayMs: 15000,
  randomDelay: true,
  maxNumberAttempts: 2,
  retryIf: (err: CombinedError) => !!err?.networkError,
}

const newUnauthorizedResponse = () =>
  new Response(
    JSON.stringify({
      data: null,
      errors: [
        {
          message: i18n._(
            msg`You are not authorized to view this page, please login to continue.`,
          ),
          extensions: {
            code: 'UNAUTHORIZED',
          },
        },
      ],
    }),
    {
      status: 401,
      statusText: 'Unauthorized',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )

export const createGraphQLClient = ({
  cacheStorage,
  accessTokenActions,
  traceRequestActions,
}: CreateGraphQLClientProps) => {
  const { fetchAccessToken, getAccessToken } = accessTokenActions
  const { getPageId, getSessionId } = traceRequestActions

  // A reload with queued offline mutations hydrates + replays them as fresh
  // operations — mark the window so their failures still count as background.
  Promise.resolve(cacheStorage.readMetadata?.())
    .then((metadata) => {
      if (Array.isArray(metadata) && metadata.length > 0) {
        offlineSyncStore.markReplayWindow()
      }
    })
    .catch((err) => {
      // No replay window, but don't hide the reason
      console.error('[OfflineSync] could not read queue metadata', err)
    })

  const exchanges: Exchange[] = [
    errorExchange({
      onError: (error, operation) => {
        error.message = error.message
          .replace(/\[Network\]|\[GraphQL\]/g, '')
          .trim()

        // A 401 is the session ending, not a bug; HTTP/2 has no status text
        const isUnauthorized = error.response?.status === 401
        if (isUnauthorized) return
        reportGraphQLError(error, operation)
      },
    }),
    refocusExchange(),
    // `offlineExchange` (not the plain `cacheExchange`) is what queues mutations
    // that fail while offline and replays them on `storage.onOnline`. It only
    // queues mutations that produced an optimistic update, so every offline
    // action needs an entry in `graphcacheConfig.optimistic`.
    offlineExchange({
      schema,
      storage: cacheStorage,
      keys: graphcacheConfig.keys,
      optimistic: graphcacheConfig.optimistic,
      updates: graphcacheConfig.updates,
    }),
    // One mutation in flight at a time — offlineExchange replays its queue
    // concurrently, which raced dependent chains (see CRE-80).
    serializeMutationsExchange,
    authExchange(async (utils): Promise<AuthConfig> => {
      // Don't attempt a (doomed) token refresh at startup while offline — it
      // would block for the full apiFetch timeout × retries.
      if (getIsOnline()) await fetchAccessToken()
      const identityHeaders = getTraceIdentityHeaders()
      return {
        addAuthToOperation(baseOperation) {
          const sessionToken = getAccessToken()
          const operation = utils.appendHeaders(baseOperation, identityHeaders)
          if (!sessionToken) {
            if (operation.context.allowUnauthorized) {
              return operation
            }
            // Offline with no access token does NOT mean "logged out" — the
            // user is still authenticated (refresh token persists in secure
            // storage). Fabricating a 401 here is what surfaced a bogus
            // "Unauthorized" on the assignments list and made offline mutations
            // look server-rejected (so they were dropped instead of queued).
            // Let the operation through: cached reads resolve from graphcache,
            // and network ops fail as a genuine [Network] error that
            // offlineExchange queues + replays on reconnect.
            if (!getIsOnline()) {
              return operation
            }
            return makeOperation(operation.kind, operation, {
              ...operation.context,
              fetch: () => Promise.resolve(newUnauthorizedResponse()),
            })
          }

          return utils.appendHeaders(operation, {
            authorization: sessionToken,
          })
        },
        async refreshAuth() {
          await fetchAccessToken(true)
        },
        willAuthError() {
          // Offline: skip the refresh trigger — refreshing can't succeed and
          // would block every operation for the apiFetch timeout. Reads serve
          // from cache; writes queue.
          if (!getIsOnline()) return false
          const token = getAccessToken()
          return !token || isTokenExpired(token)
        },
        didAuthError(error, _operation) {
          return error.response?.status === 401
        },
      }
    }),
    retryExchange(retryOptions),
    syncTrackerExchange,
    fetchExchange,
  ]
  return new Client({
    url: API_URL,
    exchanges,
    requestPolicy: 'cache-and-network',
    fetchOptions: () => ({
      headers: {
        [TRACE_HEADERS.SESSION_ID]: getSessionId(),
        [TRACE_HEADERS.PAGE_ID]: getPageId(),
        [TRACE_HEADERS.REQUEST_ID]: generateTraceId(),
      },
    }),
  })
}
